// ============================================
// sendSessionTranscript — Phase 4 of endSession
// ============================================
// Wave 5d — extracted from endSession() Phase 4.
//
// Responsibilities:
//   1. Await calendlyEventFetchRef so the actual confirmed booking time
//      (not the bot's suggested time) is captured before the n8n send.
//   2. Resolve customer name from booking → contact info → transcript fallback.
//   3. Assemble the /api/transcript payload (transcript, outcome, contact,
//      botInfo, pageContext, recording metadata, A/B testing data).
//   4. POST and flag transcriptSentRef so the unload-beacon doesn't double-fire.
//
// Caller responsibilities (kept inline):
//   - Phase 1/2/3 (teardown, outcome calc, recording upload).
//   - setIsUploading(false) + postCallInProgressRef.current = false AFTER.

import type { MutableRefObject } from 'react'
import { postTranscript } from '../api/voice-bot-client'
import type {
  ContactInfo,
  ConversationOutcome,
  Message,
  ScheduledMeeting,
  VoiceSessionData,
} from '../types'

type ContactInfoSource =
  | 'pre_session_form'
  | 'pre_calendly_form'
  | 'during_conversation_popup'
  | 'post_session_popup'
  | null

interface ConfigSnapshot {
  botName?: string
  companyName?: string
  propertyCode?: string | null
  recordingsFolderId?: string | null
  transcriptsFolderId?: string | null
  contactFormTiming?: string
  [key: string]: unknown
}

export interface SendSessionTranscriptDeps {
  // Refs
  sessionDataRef: MutableRefObject<VoiceSessionData | null>
  scheduledMeetingRef: MutableRefObject<ScheduledMeeting | null>
  bookingConfirmedRef: MutableRefObject<boolean>
  contactInfoRef: MutableRefObject<ContactInfo | null>
  contactInfoSourceRef: MutableRefObject<ContactInfoSource>
  reachedSchedulingRef: MutableRefObject<boolean>
  recordedAudioBlobRef: MutableRefObject<Blob | null>
  tokensUsedRef: MutableRefObject<number>
  calendlyEventFetchRef: MutableRefObject<Promise<void> | null>
  transcriptSentRef: MutableRefObject<boolean>
  configRef: MutableRefObject<ConfigSnapshot>
  selectedLanguageRef: MutableRefObject<string>
  // Values
  transcript: Message[]
  duration: number
  outcome: ConversationOutcome
  audioUrl: string | null
  audioStoragePath: string | null
  isAnonymous: boolean
  // Helpers
  extractCustomerNameFromTranscript: (
    transcript: { role: string; content: string }[],
  ) => string | undefined
}

export const sendSessionTranscript = async (
  deps: SendSessionTranscriptDeps,
): Promise<void> => {
  const {
    sessionDataRef,
    scheduledMeetingRef,
    bookingConfirmedRef,
    contactInfoRef,
    contactInfoSourceRef,
    reachedSchedulingRef,
    recordedAudioBlobRef,
    tokensUsedRef,
    calendlyEventFetchRef,
    transcriptSentRef,
    configRef,
    selectedLanguageRef,
    transcript,
    duration,
    outcome,
    audioUrl,
    audioStoragePath,
    isAnonymous,
    extractCustomerNameFromTranscript,
  } = deps

  try {
    // Wait for Calendly event fetch to complete (if any) so we have the ACTUAL booking time
    // Without this, scheduledMeetingRef might still have the bot's suggested time, not what the user actually picked
    if (calendlyEventFetchRef.current) {
      console.log(
        '📅 Waiting for Calendly event details before sending to n8n...',
      )
      await calendlyEventFetchRef.current
      calendlyEventFetchRef.current = null
      console.log(
        '📅 Calendly event details resolved, scheduledMeeting:',
        scheduledMeetingRef.current?.formattedTime,
      )
    }

    console.log('📤 Sending session data to /api/transcript...')
    console.log('📤 scheduledMeetingRef.current:', scheduledMeetingRef.current)
    console.log('📤 bookingConfirmedRef.current:', bookingConfirmedRef.current)
    console.log('📤 contactInfoRef.current:', contactInfoRef.current)

    // Get customer name from scheduledMeeting if available, fallback to transcript extraction
    let customerName =
      scheduledMeetingRef.current?.customerName || contactInfoRef.current?.name

    // If no name from booking flow, try to extract it from the conversation transcript
    if (!customerName && transcript.length > 1) {
      customerName = extractCustomerNameFromTranscript(transcript)
      if (customerName) {
        console.log(
          `📛 Extracted customer name from transcript: "${customerName}"`,
        )
      }
    }

    // Only include scheduledMeeting if user actually confirmed in Calendly
    // If user ended call without confirming, this prevents sending unconfirmed bookings to n8n
    const confirmedMeeting = bookingConfirmedRef.current
      ? scheduledMeetingRef.current
      : null

    const payload = {
      sessionId: sessionDataRef.current?.sessionId,
      transcript, // Hybrid transcript: Whisper v3 user + RT bot
      whisperTranscript: null, // TODO: Add if we transcribe full recording
      duration,
      outcome,
      scheduledMeeting: confirmedMeeting, // Only send if user confirmed in Calendly
      contactInfo: {
        ...contactInfoRef.current,
        name: customerName, // Include name from booking or contact info
      },
      // Bot/Customer info for n8n routing (use configRef to avoid stale closure)
      botInfo: {
        botName: configRef.current.botName, // e.g., "סיוון", "M"
        companyName: configRef.current.companyName, // e.g., "תמר ואור", "iLands"
        language: selectedLanguageRef.current, // Use actual user-selected language from pre-call picker (not config default)
        propertyCode: configRef.current.propertyCode || null, // e.g., "HAD4" if property-specific
        recordingsFolderId: configRef.current.recordingsFolderId || null, // Google Drive folder for shortcuts
        transcriptsFolderId: configRef.current.transcriptsFolderId || null,
      },
      // Page context
      pageContext: {
        url: window.location.href,
        referrer: document.referrer || null,
        userAgent: navigator.userAgent,
      },
      // Always send URL (all recordings uploaded to Supabase Storage)
      recordingUrl: audioUrl,
      recordingStoragePath: audioStoragePath,
      recordingSize: recordedAudioBlobRef.current?.size || 0,
      recordingType: 'audio/webm',
      customerId: sessionDataRef.current?.customerId,
      tokensUsed: tokensUsedRef.current,
      // A/B testing data for contact form timing experiment
      abTesting: {
        contactFormTiming: configRef.current.contactFormTiming || 'none',
        contactInfoCollected: !!contactInfoRef.current,
        contactInfoSource: contactInfoSourceRef.current,
        reachedScheduling: reachedSchedulingRef.current,
        bookingCompleted: bookingConfirmedRef.current,
        isAnonymous,
      },
    }

    const response = await postTranscript(payload)

    if (response.ok) {
      const result = await response.json()
      transcriptSentRef.current = true // Mark sent so beacon doesn't double-fire
      console.log('✅ Session data sent successfully:', result.message)
    } else {
      console.error('❌ Failed to send session data:', response.status)
    }
  } catch (err) {
    console.error('❌ Failed to send session data:', err)
  }
}
